import capitalize from "lodash/capitalize";
import { ProfileItem } from "PFApp/components/profile_item";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import Toggle from "PFComponents/toggle/toggle";
import { getProfileName } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { ActivityProfile, ActivityProfileType, Profile } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActivityProfileProp } from "./activity_profiles.types";
import css from "./activity_profiles_list.module.scss";

type ActivityProfileItemProps = {
  activityProfile: ActivityProfileProp;
  currentProfile: Profile;
  onRemove?: (activityProfile: ActivityProfileProp) => void;
  handleAddUnbiasedMode?: (profile: ActivityProfileProp) => void;
  isUnbiasedEnabled: boolean;
};

const ActivityProfileItem = ({
  activityProfile,
  currentProfile,
  onRemove,
  isUnbiasedEnabled,
  handleAddUnbiasedMode
}: ActivityProfileItemProps) => {
  const isCurrentUser = activityProfile.profile.id === currentProfile.id;
  const [obscuredRequest, setObscuredRequest] = useState(activityProfile.unbiased);
  const { t } = useTranslation(["activities", "translation"]);

  return (
    <Inline spread alignItems="center">
      <ProfileItem profile={activityProfile.profile} classes={{ root: css.profileItem }} />
      {isUnbiasedEnabled && !isCurrentUser && (
        <Toggle
          label={t("activities:parts.hideIdentity")}
          data-qa-id="Toggle"
          inline
          disabled={!handleAddUnbiasedMode}
          checked={obscuredRequest}
          onChange={(status) => {
            setObscuredRequest(status);
            handleAddUnbiasedMode?.({
              ...activityProfile,
              unbiased: status
            });
          }}
          className={css.unbiasedToggle}
        />
      )}
      {onRemove && (
        <Button
          className={css.removeButton}
          text={t(isCurrentUser ? "activities:show.parts.removeYourself" : "translation:remove")}
          aria-label={
            isCurrentUser
              ? t("activities:show.parts.removeYourself")
              : t("activities:show.parts.removeProfile", { name: getProfileName(activityProfile.profile) })
          }
          kind="secondary"
          onClick={() => onRemove(activityProfile)}
        />
      )}
    </Inline>
  );
};

type ActivityProfilesListProps = {
  activityProfiles: ActivityProfileProp[];
  title: string;
  onRemove?: null | ((activityProfile: ActivityProfileProp) => void);
  handleAddUnbiasedMode?: (profile: ActivityProfileProp) => void;
  showUnbiasedToggle?: boolean;
  isUnbiasedEnabled: boolean;
  getRemoveModalContent?: (profile: Profile) => string | undefined;
  activityProfileType?: ActivityProfileType;
};

const ActivityProfilesList = ({
  title,
  isUnbiasedEnabled,
  activityProfiles,
  onRemove,
  handleAddUnbiasedMode,
  showUnbiasedToggle,
  getRemoveModalContent,
  activityProfileType
}: ActivityProfilesListProps) => {
  const anyProfiles = activityProfiles.length !== 0;
  const { data: currentProfile } = useCurrentProfile();

  const { t } = useTranslation("activities");

  const [activityProfileToRemove, setActivityProfileToRemove] = useState<ActivityProfile | null>(null);

  const handleRemoveClick = (activityProfile: ActivityProfile) => {
    if (activityProfile.id) {
      setActivityProfileToRemove(activityProfile);
    } else {
      onRemove?.(activityProfile);
    }
  };

  const allowRemovingLast = () => {
    const isCurrentProfileLastOwner =
      activityProfiles.length === 1 && activityProfiles[0].profile.id === currentProfile.id;

    if (!activityProfileToRemove || isCurrentProfileLastOwner) {
      return false;
    }

    switch (activityProfileType) {
      case ActivityProfileType.Reviewer:
      case ActivityProfileType.Assignee:
        return activityProfileToRemove.profile.active;
      case ActivityProfileType.Owner:
        if (activityProfileToRemove.profile.active) {
          return activityProfiles?.filter(({ profile }) => profile.active).length > 1;
        } else {
          return true;
        }
      default:
        return true;
    }
  };

  const removeModalTitle = useMemo(
    () => t(`parts.remove${capitalize(activityProfileType) as Capitalize<ActivityProfileType>}`),
    [activityProfileType, t]
  );

  return (
    <>
      {anyProfiles && (
        <div>
          <div className={css.headerWrapper}>
            <div className={css.title}>
              {title}
              {activityProfiles.length >= 2 ? ` (${activityProfiles.length}):` : ":"}
            </div>
          </div>
          {activityProfiles.map((activityProfile) => (
            <ActivityProfileItem
              key={activityProfile.id}
              activityProfile={activityProfile}
              currentProfile={currentProfile}
              onRemove={handleRemoveClick}
              isUnbiasedEnabled={isUnbiasedEnabled && !!showUnbiasedToggle}
              handleAddUnbiasedMode={handleAddUnbiasedMode}
            />
          ))}
        </div>
      )}
      {activityProfileToRemove && (
        <Modal
          title={removeModalTitle}
          onOK={() => onRemove?.(activityProfileToRemove)}
          onClose={() => setActivityProfileToRemove(null)}
          showOKButton={allowRemovingLast()}
          kindOK="danger"
        >
          {getRemoveModalContent?.(activityProfileToRemove.profile) ||
            t("parts.sureWantToRemove", { profileName: getProfileName(activityProfileToRemove.profile) })}
        </Modal>
      )}
    </>
  );
};

export default ActivityProfilesList;
